import { useEffect, useState } from "react";
import WinnersModal from "./WinnersModal";
import { LuArrowLeft, LuArrowRight, LuBarChart2, LuGamepad2, LuFileCheck, LuStore, LuReceipt, LuUsers, LuAward, LuCalendarRange, LuDices } from "react-icons/lu";
import {
  useGenerateWinnersMutation,
  useGetGameDetailsQuery,
  useLazyGetPotentialWinnersQuery,
} from "../store/services/prizeServices";
import { useDispatch } from "react-redux";
import { openModalWithTimeout } from "../store/alertSlice";
import { useLazyGetApprovedReceiptByIdQuery } from "../store/services/receiptsServices";
import ReceiptDrawStage from "./ReceiptDrawLab/ReceiptDrawStage";
import UsersModal from "./prizes/UsersModal";
import { Loading } from "./Loading";
import DeleteConfirm from "./DeleteConfirm";
import { hasMinimumRole, ROLES } from "../utils/roleUtils";
import { useSearchParams } from "react-router-dom";

import ListPageShell from "./list-page/ListPageShell";
import ListPageHeader from "./list-page/ListPageHeader";

const FISCAL_PARITY_LABEL = {
  all: "Të gjithë",
  odd: "Tek",
  even: "Çift",
};

const TYPE_LABEL = {
  lottery: "Lottery",
  cashback: "Cashback",
  business_draw: "Business Draw",
};

const Tab = ({ icon = null, title, value = null, handlePreviousClick = null, handleNextClick = null, handleClick = null }) => {
  const isClickable = !!handleClick;
  return (
    <div
      className={`bg-white border border-gray-200 rounded-xl shadow-sm transition-shadow ${isClickable ? "cursor-pointer hover:shadow-md hover:border-[var(--secondary-color)]" : ""
        }`}
      onClick={handleClick || undefined}
    >
      <div className="p-5">
        <div className="flex items-start gap-4">
          <div className="bg-gray-50 rounded-lg p-3 text-gray-500 shrink-0">{icon}</div>
          <div className="flex flex-col gap-2 w-full min-w-0">
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{title}</div>
            {handlePreviousClick ? (
              <div className="flex gap-3 items-center">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePreviousClick(e);
                  }}
                  className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors shrink-0"
                  type="button"
                >
                  <LuArrowLeft size={14} className="text-gray-600" />
                </button>
                <div className="text-lg sm:text-xl font-bold text-gray-900 truncate flex-1 text-center" title={value}>
                  {value || "—"}
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleNextClick(e);
                  }}
                  className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors shrink-0"
                  type="button"
                >
                  <LuArrowRight size={14} className="text-gray-600" />
                </button>
              </div>
            ) : (
              <div className="text-lg sm:text-xl font-bold text-gray-900 break-words" title={value}>
                {value || "—"}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/* The API answers in English — show its generate-winners messages in Albanian.
   Anything not listed here falls back to GENERATE_ERROR_FALLBACK. */
const GENERATE_ERROR_FALLBACK = "Ndodhi një gabim gjatë gjenerimit të fituesve!";

/* Success with no winners: the period is over, so the backend closed the tier (and the game, if it was the last tier) */
const TIER_CLOSED_MESSAGE =
  "There are no users who have fullfilled the conditions for this tier, so it was closed without winners!";

/* Day after end_date, local time — from then on an empty tier can be closed without winners */
const isPeriodOver = (game) => {
  if (!game?.end_date) return false;
  const end = new Date(game.end_date);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return end < today;
};

const GENERATE_MESSAGES_SQ = {
  "Lottery game not found!": "Loja nuk u gjet!",
  "Lottery tier not found!": "Niveli nuk u gjet!",
  "Winners have already been generated for this tier!": "Fituesit për këtë nivel janë gjeneruar tashmë!",
  "The selected tier does not have a valid winner limit!": "Niveli i zgjedhur nuk ka limit të vlefshëm të fituesve!",
  "There are no users who have fullfilled the conditions to win a prize for this tier!":
    "Nuk ka fitues që plotësojnë kushtet për këtë nivel!",
  "There are no users who have fullfilled the conditions to win a prize for this game!":
    "Nuk ka fitues që plotësojnë kushtet për këtë lojë!",
  "Failed to generate random winners!": "Gjenerimi i fituesve dështoi!",
  "Cashback game not found!": "Loja cashback nuk u gjet!",
  "This cashback game is no longer active!": "Kjo lojë cashback nuk është më aktive!",
  "This lottery game is no longer active!": "Kjo lojë me lotari nuk është më aktive!",
  "All the lower tiers must be drawn before the highest tier!":
    "Të gjitha nivelet më të ulëta duhet të tërhiqen para nivelit më të lartë!",
  [TIER_CLOSED_MESSAGE]: "Askush nuk i plotësoi kushtet për këtë nivel, prandaj niveli u mbyll pa fitues.",
  "An error occurred trying to generate winners!": GENERATE_ERROR_FALLBACK,
};

const toAlbanian = (message, fallback = GENERATE_ERROR_FALLBACK) =>
  GENERATE_MESSAGES_SQ[message] || fallback;

/* generate-winners returns [] when nobody qualifies, otherwise
   { winners, business_winners, skipped_businesses } — `winners` are the drawn
   users, `business_winners` the shops behind their receipts that also won. */
const extractWinners = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.winners)) return data.winners;
  return null;
};

const formatDateTime = (d) =>
  d
    ? new Date(d).toLocaleString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : null;

/* Shapes a drawn row (+ its receipt from /admin/receipts/:id, when it could be loaded)
   the way ReceiptDrawStage prints it */
const toStageWinner = (w, receipt, game) => {
  const isBusiness = game?.type === "business_draw";
  const prize =
    game?.type === "cashback"
      ? w.cashback_amount
      : isBusiness
        ? Number(game?.business_prize_amount) > 0 ? game.business_prize_amount : null
        : w.prize_amount;
  return {
    ...w,
    ...(isBusiness ? { first_name: w.business_name, last_name: "" } : {}),
    business_nui: receipt?.NUI || w.business_nui || w.nui,
    business_name: receipt?.business_name || w.business_name,
    coupon_id: receipt?.coupon_id,
    receipt_type: receipt?.type,
    receipt_date: formatDateTime(receipt?.release_date || receipt?.created_at),
    total: receipt?.total_with_vat ?? (game?.type === "cashback" ? w.amount_count : null),
    vat: receipt?.vat_amount ?? null,
    items: (receipt?.items || []).map((i) => ({
      name: i.Name,
      qty: i.Quantity,
      amount: Number(i.Total) / 100,
    })),
    prize_amount: prize,
    prize_desc: isBusiness ? game?.business_prize_desc : undefined,
  };
};

export const GenerateWinners = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [games, setGames] = useState([]);
  const [activeTierIndex, setActiveTierIndex] = useState(0);
  const [activeGameIndex, setActiveGameIndex] = useState(0);
  const [selectedGame, setSelectedGame] = useState({ type: "" });
  const [selectedTier, setSelectedTier] = useState(null);
  const [searchParams] = useSearchParams();
  const game_id = searchParams.get("game_id");

  const [isGenerateConfirmOpen, setIsGenerateConfirmOpen] = useState(false);
  const [winners, setWinners] = useState([]);
  /* Lottery only: the businesses that won with the drawn receipts (undefined = no business tab) */
  const [businessWinners, setBusinessWinners] = useState(undefined);
  /* The receipt draw stage: winners stay null until the draw (and the receipts' details) are back */
  const [drawRun, setDrawRun] = useState(null); // { key, winners }
  const [pastWinnersExcluded, setPastWinnersExcluded] = useState(true);
  const [isUsersModalOpen, setIsUsersModalOpen] = useState(false);
  const [potentialWinnersCount, setPotentialWinnersCount] = useState(null);
  const [businessDrawLimit, setBusinessDrawLimit] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  /* Tiers drawn during this session ("game_id:tier_level"), since the games
     list (and its has_ended flags) isn't refetched after a draw */
  const [generatedTiers, setGeneratedTiers] = useState([]);

  const dispatch = useDispatch();

  const { data, isLoading } = useGetGameDetailsQuery({}, { refetchOnMountOrArgChange: true });
  const [fetchPotentialWinners, { data: potWinners }] = useLazyGetPotentialWinnersQuery();
  const [generateWinners] = useGenerateWinnersMutation();
  const [fetchReceipt] = useLazyGetApprovedReceiptByIdQuery();

  /* Set games + selected game */
  useEffect(() => {
    if (data?.data) {
      setGames(data.data);
      if (game_id) {
        setSelectedGame(data.data.find((game) => game.game_id == game_id));
      } else {
        setSelectedGame(data.data[0]);
      }
    }
  }, [data, game_id]);

  /* Reset tier when game changes */
  useEffect(() => {
    if (selectedGame && selectedGame.tiers) {
      setSelectedTier(selectedGame.tiers[0]);
    } else {
      setSelectedTier({});
    }
    setActiveTierIndex(0);
    setWinners([]);
    setBusinessWinners(undefined);
  }, [selectedGame]);

  /* Winners belong to one tier — clear them when switching tiers */
  useEffect(() => {
    setWinners([]);
    setBusinessWinners(undefined);
  }, [selectedTier]);

  const tierKey = `${selectedGame?.game_id}:${selectedTier?.tier_level}`;
  const isTierAlreadyGenerated =
    selectedGame?.type === "lottery" &&
    (Number(selectedTier?.has_ended) === 1 || generatedTiers.includes(tierKey));
  const isAlreadyGenerated = winners.length > 0 || isTierAlreadyGenerated;

  /* Fetch potential-winner count for current game/tier
     - lottery → tier-aware
     - cashback / business_draw → no tier */
  useEffect(() => {
    if (!selectedGame?.type) return;

    const fetchData = async () => {
      try {
        const response = await fetchPotentialWinners({
          type: selectedGame.type,
          game_id: selectedGame.game_id,
          ...(selectedTier?.tier_level ? { tier_level: selectedTier.tier_level } : {}),
        }).unwrap();
        setPotentialWinnersCount(response?.data?.length ?? 0);
      } catch {
        setPotentialWinnersCount(0);
      }
    };

    if (selectedGame.type === "lottery" && selectedTier?.tier_level) {
      fetchData();
    } else if (selectedGame.type === "cashback" || selectedGame.type === "business_draw") {
      fetchData();
    }
  }, [selectedGame, selectedTier, fetchPotentialWinners]);

  /* With nobody qualifying, a lottery tier whose period is over can still be "drawn": the backend closes it without winners */
  const canCloseEmptyTier = selectedGame?.type === "lottery" && isPeriodOver(selectedGame);
  const willCloseEmptyTier = canCloseEmptyTier && (potentialWinnersCount ?? 0) < 1;

  const handleButtonClick = (e) => {
    e.preventDefault();
    if (isAlreadyGenerated) return;
    if ((potentialWinnersCount ?? 0) < 1 && !canCloseEmptyTier) {
      dispatch(openModalWithTimeout({ text: "Nuk ka fitues të kushtit për këtë nivel", type: "error" }));
      return;
    }
    /* Nothing to draw — just confirm closing the tier; otherwise the draw stage is the confirmation */
    if (willCloseEmptyTier) {
      setIsGenerateConfirmOpen(true);
      return;
    }
    setDrawRun({ key: Date.now(), winners: null });
  };

  const getDrawLimit = () => {
    if (selectedGame?.type !== "business_draw") return selectedTier?.limit;
    const gameLimit = parseInt(selectedGame?.limit, 10);
    if (Number.isFinite(gameLimit) && gameLimit > 0) return gameLimit;
    const parsed = parseInt(businessDrawLimit, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : potentialWinnersCount;
  };

  /* Runs the draw. Returns { drawn, businessWinners }, or null (after alerting) when nobody was drawn */
  const runGenerate = async () => {
    setIsGenerating(true);
    let response;
    try {
      const result = await generateWinners({
        type: selectedGame?.type,
        game_id: selectedGame?.game_id,
        ...(selectedTier?.tier_level ? { tier_level: selectedTier.tier_level } : {}),
        limit: getDrawLimit(),
        past_winners_excluded: pastWinnersExcluded,
      });
      response = result.data ?? result.error?.data;
    } finally {
      setIsGenerating(false);
    }

    const drawn = extractWinners(response?.data);

    if (response?.success === false || !drawn) {
      dispatch(
        openModalWithTimeout(
          { text: toAlbanian(response?.message || response?.error), type: "error" },
          4000
        )
      );
      return null;
    }

    if (drawn.length === 0 && response?.message === TIER_CLOSED_MESSAGE) {
      setGeneratedTiers((prev) => [...prev, tierKey]);
      dispatch(openModalWithTimeout({ text: toAlbanian(response.message), type: "success" }, 4000));
      return null;
    }

    if (drawn.length === 0) {
      dispatch(
        openModalWithTimeout(
          { text: toAlbanian(response.message, "Nuk ka fitues që plotësojnë kushtet për këtë nivel!"), type: "error" },
          4000
        )
      );
      return null;
    }

    if (selectedGame?.type === "lottery") {
      setGeneratedTiers((prev) => [...prev, tierKey]);
    }

    /* Businesses past the game's business winners limit are skipped, not drawn */
    const limitSkipped = (response?.data?.skipped_businesses || []).filter(
      (b) => b.reason === "limit_reached"
    ).length;
    if (limitSkipped > 0) {
      dispatch(
        openModalWithTimeout(
          {
            text: `Limiti i bizneseve fituese u arrit: ${limitSkipped} biznes${limitSkipped === 1 ? "" : "e"} nuk u shpallën fitues.`,
            type: "success",
          },
          5000
        )
      );
    }

    setWinners(drawn);
    /* Lottery draws also return the businesses behind the winning receipts; cashback returns [] */
    const businessWinners = Array.isArray(response?.data?.business_winners)
      ? response.data.business_winners
      : undefined;
    return { drawn, businessWinners };
  };

  const handleConfirmGenerate = async () => {
    setIsGenerateConfirmOpen(false);
    await runGenerate();
  };

  /* "Fillo tërheqjen" on the stage: the pile shuffles while the draw runs, then the
     winners' receipts are loaded so the stage can print them (missing ones print without details) */
  const handleDrawStart = async () => {
    const result = await runGenerate();
    if (!result) {
      setDrawRun(null);
      return;
    }
    const { drawn, businessWinners } = result;
    const receipts = await Promise.all(
      drawn.map((w) =>
        w.receipt_id
          ? fetchReceipt({ id: w.receipt_id }, true)
              .unwrap()
              .then((res) => res?.data || null)
              .catch(() => null)
          : null
      )
    );
    const rows = drawn.map((w, i) => toStageWinner(w, receipts[i], selectedGame));
    /* business_winners rows are winning receipt rows: their business name comes from that receipt */
    const businessRows =
      selectedGame?.type === "lottery" && businessWinners
        ? businessWinners.map((b) => {
            const receipt = receipts[drawn.findIndex((w) => w.receipt_id === b.receipt_id)];
            return {
              receipt_id: b.receipt_id,
              business_nui: b.business_nui,
              business_name: receipt?.business_name || b.business_name,
              prize_amount:
                Number(selectedGame?.business_prize_amount) > 0 ? selectedGame.business_prize_amount : null,
              prize_desc: selectedGame?.business_prize_desc,
            };
          })
        : undefined;
    setBusinessWinners(businessRows);
    setDrawRun((run) => run && { ...run, winners: rows, businessWinners: businessRows });
  };

  const closeModal = () => setIsModalOpen(false);

  const handleNextTierClick = (e) => {
    e.preventDefault();
    const updatedIndex = activeTierIndex + 1;
    if (updatedIndex > selectedGame.tiers.length - 1) {
      setActiveTierIndex(0);
      setSelectedTier(selectedGame.tiers[0]);
    } else {
      setActiveTierIndex(updatedIndex);
      setSelectedTier(selectedGame.tiers[updatedIndex]);
    }
  };

  const handlePreviousTierClick = () => {
    const updatedIndex = activeTierIndex - 1;
    if (updatedIndex < 0) {
      setActiveTierIndex(selectedGame?.tiers?.length - 1);
      setSelectedTier(selectedGame?.tiers[selectedGame.tiers.length - 1]);
    } else {
      setActiveTierIndex(updatedIndex);
      setSelectedTier(selectedGame.tiers[updatedIndex]);
    }
  };

  const handleNextGameClick = () => {
    const updatedIndex = activeGameIndex + 1;
    if (updatedIndex > games.length - 1) {
      setActiveGameIndex(0);
      setSelectedGame(games[0]);
    } else {
      setActiveGameIndex(updatedIndex);
      setSelectedGame(games[updatedIndex]);
    }
  };

  const handlePreviousGameClick = () => {
    const updatedIndex = activeGameIndex - 1;
    if (updatedIndex < 0) {
      setActiveGameIndex(games.length - 1);
      setSelectedGame(games[games.length - 1]);
    } else {
      setActiveGameIndex(updatedIndex);
      setSelectedGame(games[updatedIndex]);
    }
  };

  if (isLoading) return <Loading />;

  if (!data) {
    return (
      <div className="w-full h-full flex items-center justify-center italic text-xl text-gray-400">
        Për momentin nuk ka asnjë lojë që mund të gjenerojë fitues
      </div>
    );
  }

  if (!selectedGame) {
    return (
      <ListPageShell>
        <ListPageHeader
          eyebrow={{ icon: <LuDices size={18} />, label: "Gjenerimi i fituesve" }}
          title="Gjenero fituesit"
          subtitle="Zgjidh lojën dhe gjenero fituesit sipas kushteve të saj."
        />

        <div className="flex-1 min-h-[72vh] flex items-center justify-center rounded-2xl bg-white p-8 sm:p-12">
          <div className="flex flex-col items-center text-center max-w-md mx-auto gap-5">
            <div className="relative">
              <div className="absolute inset-0 rounded-full bg-[var(--secondary-color)]/10 blur-xl" />
              <div className="relative flex items-center justify-center w-20 h-20 rounded-full bg-[var(--secondary-color)]/10 text-[var(--secondary-color)]">
                <LuGamepad2 size={36} />
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
                Nuk ka lojë aktive
              </h2>
              <p className="text-sm text-gray-500 leading-relaxed">
                Për momentin nuk ka asnjë lojë aktive për të gjeneruar fitues. Krijo një lojë të re ose prit deri sa një lojë ekzistuese të bëhet aktive.
              </p>
            </div>
          </div>
        </div>

        <WinnersModal isOpen={isModalOpen} handleIsOpen={closeModal} winners={winners} businessWinners={businessWinners} isBusiness={selectedGame?.type === "business_draw"} />
      </ListPageShell>
    );
  }

  const isLottery = selectedGame.type === "lottery";
  const isCashback = selectedGame.type === "cashback";
  const isBusinessDraw = selectedGame.type === "business_draw";

  /* Receipts in the draw: what the qualifying users have, falling back to their count */
  const drawPoolSize =
    (potWinners?.data || []).reduce((sum, u) => sum + (Number(u.receipt_count) || 0), 0) ||
    potentialWinnersCount ||
    0;
  const drawLimit = getDrawLimit();
  const readyText = isCashback
    ? `Të gjithë ${potentialWinnersCount ?? 0} pjesëmarrësit që i plotësojnë kushtet fitojnë cashback.`
    : `Do të tërhiqen deri në ${drawLimit ?? potentialWinnersCount ?? 0} fitues nga ${potentialWinnersCount ?? 0} ${
        isBusinessDraw ? "biznese potenciale" : "pjesëmarrës potencialë"
      }${pastWinnersExcluded ? ", pa fituesit e mëparshëm" : ""}. Tërheqja nuk mund të kthehet mbrapsht.`;

  return (
    <>
      <DeleteConfirm
        title="Mbyll nivelin pa fitues?"
        description="Periudha e lojës ka përfunduar dhe askush nuk i plotëson kushtet për këtë nivel. Niveli do të mbyllet pa fitues."
        confirmLabel="Mbyll nivelin"
        isOpen={isGenerateConfirmOpen}
        closeModal={() => setIsGenerateConfirmOpen(false)}
        handleConfirm={handleConfirmGenerate}
      />
      <WinnersModal isOpen={isModalOpen} handleIsOpen={closeModal} winners={winners} businessWinners={businessWinners} isBusiness={selectedGame?.type === "business_draw"} />
      <UsersModal
        isOpen={isUsersModalOpen}
        handleClose={() => setIsUsersModalOpen(false)}
        users={potWinners?.data || []}
      />

      <ListPageShell>
        <ListPageHeader
          eyebrow={{ icon: <LuDices size={18} />, label: "Gjenerimi i fituesve" }}
          title="Gjenero fituesit"
          subtitle="Zgjidh lojën dhe gjenero fituesit sipas kushteve të saj."
        />

        {/* Stats grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Tab
            title="Loja"
            value={selectedGame?.title}
            icon={<LuGamepad2 size={22} />}
            handlePreviousClick={handlePreviousGameClick}
            handleNextClick={handleNextGameClick}
          />
          <Tab title="Tipi" value={TYPE_LABEL[selectedGame?.type] || selectedGame?.type} icon={<LuGamepad2 size={22} />} />
          <Tab
            title="Periudha kohore"
            value={`${new Date(selectedGame?.start_date).toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "2-digit",
            })} - ${new Date(selectedGame?.end_date).toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "2-digit",
            })}`}
            icon={<LuCalendarRange size={22} />}
          />

          {/* Lottery-specific */}
          {isLottery && (
            <Tab
              title="Kushti"
              value={selectedTier?.tier_level}
              icon={<LuBarChart2 size={22} />}
              handlePreviousClick={handlePreviousTierClick}
              handleNextClick={handleNextTierClick}
            />
          )}
          {isLottery && (
            <Tab title="Numri i kuponave" value={selectedTier?.required_receipts} icon={<LuFileCheck size={22} />} />
          )}
          {isLottery && (
            <Tab title="Shuma e kuponave" value={`${selectedTier?.required_amount || 0}€`} icon={<LuReceipt strokeWidth={1.5} size={22} />} />
          )}
          {isLottery && (
            <Tab title="Limiti i fituesve" value={`${selectedTier?.limit || 0}`} icon={<LuReceipt strokeWidth={1.5} size={22} />} />
          )}
          {isLottery && (
            <Tab title="Shpërblimi" value={`${selectedTier?.prize_amount}€`} icon={<LuAward size={22} />} />
          )}

          {/* Cashback-specific */}
          {isCashback && (
            <Tab title="Përqindja" value={`${selectedGame?.cashback_percentage}%`} icon={<LuAward size={22} />} />
          )}

          {/* Business-draw-specific */}
          {isBusinessDraw && (
            <Tab
              title="Përzgjedhje sipas"
              value={FISCAL_PARITY_LABEL[selectedGame?.fiscal_parity] || selectedGame?.fiscal_parity}
              icon={<LuStore size={22} />}
            />
          )}
          {isBusinessDraw && (
            <Tab
              title="Shuma fituese për biznes"
              value={
                selectedGame?.business_prize_amount && Number(selectedGame.business_prize_amount) > 0
                  ? `${selectedGame.business_prize_amount}€`
                  : selectedGame?.business_prize_desc
                    ? selectedGame.business_prize_desc
                    : "—"
              }
              icon={<LuAward size={22} />}
            />
          )}
          {isBusinessDraw && selectedGame?.limit > 0 && (
            <Tab
              title="Limiti i fituesve"
              value={`${selectedGame.limit}`}
              icon={<LuReceipt strokeWidth={1.5} size={22} />}
            />
          )}

          {/* Potential winners — applies to all */}
          <Tab
            title={isBusinessDraw ? "Biznese potenciale" : "Fitues potencial"}
            value={potentialWinnersCount ?? "—"}
            icon={<LuUsers size={22} />}
            handleClick={() => (potentialWinnersCount > 0 ? setIsUsersModalOpen(true) : null)}
          />
        </div>

        {/* Action card */}
        {hasMinimumRole(ROLES.MANAGER) && (
          <div className="rounded-2xl border border-gray-200 bg-white shadow-sm p-5 sm:p-6">
            <div className="flex flex-col gap-5 max-w-xl mx-auto">
              {!isAlreadyGenerated && (
                <label className="flex items-center gap-3 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    className="w-4 h-4 accent-[var(--secondary-color)]"
                    checked={pastWinnersExcluded}
                    onChange={(e) => setPastWinnersExcluded(e.target.checked)}
                  />
                  <span className="text-sm font-medium text-gray-700">Përjashto fituesit e mëparshëm</span>
                </label>
              )}

              {isBusinessDraw && !isAlreadyGenerated && !(selectedGame?.limit > 0) && (
                <div className="flex flex-col gap-1">
                  <label htmlFor="business-limit" className="text-xs font-semibold text-gray-700">
                    Numri maksimal i fituesve (opsionale)
                  </label>
                  <input
                    id="business-limit"
                    type="number"
                    min="1"
                    value={businessDrawLimit}
                    onChange={(e) => setBusinessDrawLimit(e.target.value)}
                    placeholder={`Lëre bosh për të gjithë (${potentialWinnersCount ?? 0})`}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-[var(--secondary-color)]/30 focus:border-[var(--secondary-color)]"
                  />
                </div>
              )}
              {/* {isBusinessDraw && winners.length <= 0 && selectedGame?.limit > 0 && (
                <div className="rounded-lg border border-[var(--secondary-color)]/30 bg-[var(--secondary-color)]/5 px-3 py-2 text-xs text-gray-700">
                  Limiti i lojës është caktuar në <span className="font-semibold">{selectedGame.limit}</span> fitues. Ky numër do të përdoret automatikisht.
                </div>
              )} */}

              <button
                onClick={handleButtonClick}
                disabled={isAlreadyGenerated || isGenerating}
                className="inline-flex items-center justify-center gap-3 rounded-xl bg-[var(--secondary-color)] hover:bg-[var(--secondary-color)]/90 active:scale-[0.99] px-4 py-3.5 text-base font-bold text-white shadow-sm transition disabled:bg-gray-100 disabled:text-gray-400 disabled:font-medium disabled:cursor-not-allowed"
                type="button"
              >
                {winners.length > 0 ? (
                  <span>Lista e fituesve u gjenerua me sukses!</span>
                ) : isTierAlreadyGenerated ? (
                  <span>Fituesit për këtë nivel janë gjeneruar tashmë</span>
                ) : isGenerating ? (
                  <span>Duke gjeneruar...</span>
                ) : (
                  <>
                    <LuDices size={20} />
                    <span>Gjenero fituesit</span>
                  </>
                )}
              </button>

              {winners.length > 0 && (
                <button
                  type="button"
                  onClick={() => setIsModalOpen(true)}
                  className="text-sm text-[var(--secondary-color)] font-semibold hover:underline self-center"
                >
                  Hap listën e fituesve
                </button>
              )}
            </div>
          </div>
        )}
      </ListPageShell>

      {drawRun && (
        <ReceiptDrawStage
          key={drawRun.key}
          winners={drawRun.winners}
          businessWinners={drawRun.businessWinners}
          totalReceipts={drawPoolSize}
          subtitle={`${selectedGame?.title || ""}${isLottery ? ` · Kushti ${selectedTier?.tier_level}` : ""}`}
          readyTitle="Gjenero fituesit?"
          readyText={readyText}
          onStart={handleDrawStart}
          onClose={() => setDrawRun(null)}
          onShowList={() => {
            setDrawRun(null);
            setIsModalOpen(true);
          }}
          isBusiness={isBusinessDraw}
        />
      )}
    </>
  );
};
